/* eslint-disable unicorn/no-null -- Local cache records persist `null` values to represent empty database slots. */

import { type Collection, createCollection } from '@tanstack/db';
import type { RxCollection, RxDatabase, RxJsonSchema, RxStorage } from 'rxdb';
import { addRxPlugin, createRxDatabase } from 'rxdb';
import { disableWarnings, RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { rxdbCollectionOptions } from '@/lib/cache/databases/rxdb-collection-options';
import { registerDebugNamespace } from '@/lib/debug/registerDebugNamespace';
import { isProductionRuntime } from '@/lib/runtime-flags';
import { isPlaywrightRuntime } from '@/lib/runtime-mode';

const METADATA_COLLECTION_ID = 'metadata';
const SCHEMA_VERSION_KEY = 'schema-version';
const ADAPTER_KEY = 'adapter';
const SCHEMA_VERSION = 2;
const CLEANUP_INTERVAL_MS = 5 * 60 * 1000;
export const LOCAL_CACHE_PRUNE_INTERVAL_MS = CLEANUP_INTERVAL_MS;

export type LocalCacheAdapter = 'dexie' | 'sqlite' | 'memory';

type GenericDocument = Record<string, unknown>;

type MetadataDocument = {
  key: string;
  valueNumber?: number;
  valueString?: string;
};

type RegisteredCollectionEntry<T extends Record<string, unknown>> = {
  schema: RxJsonSchema<T>;
  buildTanStackCollection: (rxCollection: RxCollection<T>) => Collection<T, string>;
  onReady?: (context: {
    adapter: LocalCacheAdapter;
    rxCollection: RxCollection<T>;
    collection: Collection<T, string>;
  }) => void;
  prune?: (context: { adapter: LocalCacheAdapter; rxCollection: RxCollection<T> }) => Promise<void> | void;
  startSync: boolean;
};

type AnyDocument = Record<string, unknown>;
type AnyRxCollection = RxCollection<AnyDocument>;
type AnyTanStackCollection = Collection<AnyDocument, string>;

interface LocalCacheState {
  adapter: LocalCacheAdapter;
  collections: Map<string, AnyRxCollection>;
  database: RxDatabase;
  tanstackCollections: Map<string, AnyTanStackCollection>;
}

interface GlobalState {
  initPromise: Promise<LocalCacheState> | null;
  pruneTimer: ReturnType<typeof globalThis.setInterval> | null;
  registry: Map<string, unknown>;
  resolved: LocalCacheState | null;
  sqliteStorageFactory: (() => Promise<{ storage: RxStorage<unknown, unknown>; multiInstance?: boolean }>) | null;
}

const LOCAL_CACHE_STATE_SYMBOL = Symbol.for('sweetistics.localCacheState');

type CacheAugmentedGlobal = typeof globalThis & {
  [LOCAL_CACHE_STATE_SYMBOL]?: GlobalState;
  __SWEETISTICS_FORCE_MEMORY_RXDB__?: boolean;
  __SWEETISTICS_LOCAL_CACHE_DB_NAME__?: string;
  __SWEETISTICS_SKIP_SCHEMA_RESET__?: boolean;
};

const cacheGlobal = globalThis as CacheAugmentedGlobal;
if (process !== undefined) {
  // biome-ignore lint/style/noProcessEnv: Test-only flag that controls RxDB adapter selection
  const { __SWEETISTICS_FORCE_MEMORY_RXDB__: forceMemoryRxdbFlag } = process.env;
  if (forceMemoryRxdbFlag === '1') {
    cacheGlobal.__SWEETISTICS_FORCE_MEMORY_RXDB__ = true;
  }
}

if (isPlaywrightRuntime()) {
  cacheGlobal.__SWEETISTICS_FORCE_MEMORY_RXDB__ = true;
}

const globalState: GlobalState = (() => {
  const existing = cacheGlobal[LOCAL_CACHE_STATE_SYMBOL];
  if (existing) {
    return existing;
  }
  const state: GlobalState = {
    initPromise: null,
    resolved: null,
    registry: new Map<string, unknown>(),
    pruneTimer: null,
    sqliteStorageFactory: null,
  };
  cacheGlobal[LOCAL_CACHE_STATE_SYMBOL] = state;
  return state;
})();

const DB_NAME =
  typeof cacheGlobal.__SWEETISTICS_LOCAL_CACHE_DB_NAME__ === 'string'
    ? cacheGlobal.__SWEETISTICS_LOCAL_CACHE_DB_NAME__
    : 'sweetistics-local-cache';

const debug: Console | null = console === undefined ? null : console;
if (debug) {
  registerDebugNamespace('localCacheDb', {
    log: (...arguments_: unknown[]) => {
      debug.log('[local-cache]', ...arguments_);
    },
    warn: (...arguments_: unknown[]) => {
      debug.warn('[local-cache]', ...arguments_);
    },
  });
}

if (!isProductionRuntime()) {
  const suppressDevelopmentWarning = () => {
    try {
      disableWarnings();
    } catch (error: unknown) {
      debug?.log?.('RxDB disableWarnings failed', error);
    }
  };
  suppressDevelopmentWarning();
  try {
    addRxPlugin(RxDBDevModePlugin);
  } catch (error: unknown) {
    debug?.log?.('RxDB dev plugin already registered', error);
  }
  // Suppress the dev-mode banner while preserving the runtime assertions.
  suppressDevelopmentWarning();
  if (console !== undefined && typeof console.warn === 'function') {
    const originalWarn = console.warn as typeof console.warn & { __sweetisticsRxdbMuted?: boolean };
    if (!originalWarn.__sweetisticsRxdbMuted) {
      // Filter the noisy Dexie upsell without muting other warnings.
      const filteredWarn: typeof console.warn & { __sweetisticsRxdbMuted?: boolean } = function consoleWarnFilter(
        this: typeof console,
        ...arguments_: Parameters<typeof console.warn>
      ) {
        const messageCandidate: unknown = arguments_[0];
        if (typeof messageCandidate === 'string' && messageCandidate.includes('RxDB Open Core RxStorage')) {
          return;
        }
        originalWarn.apply(this, arguments_);
      } as typeof console.warn & { __sweetisticsRxdbMuted?: boolean };
      filteredWarn.__sweetisticsRxdbMuted = true;
      console.warn = filteredWarn;
    }
  }
}

type StorageResolution = {
  adapter: LocalCacheAdapter;
  storage: RxStorage<unknown, unknown>;
  multiInstance: boolean;
};

function isReactNativeRuntime(): boolean {
  if (navigator === undefined) {
    return false;
  }
  const userAgent = typeof navigator.userAgent === 'string' ? navigator.userAgent : '';
  return userAgent.includes('ReactNative');
}

function assertRegisteredCollectionEntry<T extends Record<string, unknown>>(
  entry: unknown,
  collectionId: string
): asserts entry is RegisteredCollectionEntry<T> {
  if (!entry || typeof entry !== 'object') {
    throw new TypeError(`Collection "${collectionId}" has not been registered.`);
  }
  const candidate = entry as Partial<RegisteredCollectionEntry<T>>;
  if (typeof candidate.schema !== 'object' || typeof candidate.buildTanStackCollection !== 'function') {
    throw new TypeError(`Collection "${collectionId}" has not been registered.`);
  }
}

function getRegisteredCollectionEntry<T extends Record<string, unknown>>(
  entry: unknown,
  collectionId: string
): RegisteredCollectionEntry<T> {
  if (!entry) {
    throw new TypeError(`Collection "${collectionId}" has not been registered.`);
  }
  assertRegisteredCollectionEntry<T>(entry, collectionId);
  return entry;
}

async function resolveStorage(): Promise<StorageResolution> {
  if (cacheGlobal.__SWEETISTICS_FORCE_MEMORY_RXDB__) {
    const memoryModule = await import('rxdb/plugins/storage-memory');
    const storage = wrappedValidateAjvStorage({ storage: memoryModule.getRxStorageMemory() });
    return {
      adapter: 'memory',
      storage,
      multiInstance: false,
    } satisfies StorageResolution;
  }

  if (isReactNativeRuntime() && globalState.sqliteStorageFactory) {
    const resolved = await globalState.sqliteStorageFactory();
    const storage = wrappedValidateAjvStorage({ storage: resolved.storage });
    return {
      adapter: 'sqlite',
      storage,
      multiInstance: resolved.multiInstance ?? false,
    } satisfies StorageResolution;
  }

  if (globalThis.window !== undefined) {
    const { getRxStorageDexie } = await import('rxdb/plugins/storage-dexie');
    const storage = wrappedValidateAjvStorage({ storage: getRxStorageDexie() });
    return {
      adapter: 'dexie',
      storage,
      multiInstance: true,
    } satisfies StorageResolution;
  }

  const memoryModule = await import('rxdb/plugins/storage-memory');
  const storage = wrappedValidateAjvStorage({ storage: memoryModule.getRxStorageMemory() });
  return {
    adapter: 'memory',
    storage,
    multiInstance: false,
  } satisfies StorageResolution;
}

async function createDatabase(): Promise<LocalCacheState> {
  const { storage, multiInstance, adapter } = await resolveStorage();

  const database = await createRxDatabase({
    name: DB_NAME,
    storage,
    multiInstance,
    eventReduce: true,
    ignoreDuplicate: false,
  });

  const metadataSchema: RxJsonSchema<MetadataDocument> = {
    title: 'local-cache-metadata',
    version: 0,
    type: 'object',
    primaryKey: {
      key: 'key',
      fields: ['key'],
      separator: '||',
    },
    properties: {
      key: { type: 'string', maxLength: 120 },
      valueNumber: { type: 'number' },
      valueString: { type: 'string', maxLength: 2048 },
    },
    required: ['key'],
    additionalProperties: false,
  };

  // Keep metadata in the same database so we can track adapters + schema versions without extra state.
  const added = await database.addCollections({
    [METADATA_COLLECTION_ID]: { schema: metadataSchema },
  });

  const metadataCollectionCandidate: unknown = added[METADATA_COLLECTION_ID];
  assertRxCollection<MetadataDocument>(metadataCollectionCandidate, METADATA_COLLECTION_ID);
  const metadataCollection: RxCollection<MetadataDocument> = metadataCollectionCandidate;
  const versionDocument = await metadataCollection.findOne(SCHEMA_VERSION_KEY).exec();
  const rawStoredVersion: unknown = versionDocument?.get('valueNumber');
  const storedVersion = typeof rawStoredVersion === 'number' ? rawStoredVersion : null;

  if (
    !cacheGlobal.__SWEETISTICS_SKIP_SCHEMA_RESET__ &&
    typeof storedVersion === 'number' &&
    storedVersion !== SCHEMA_VERSION
  ) {
    // When the schema bumps we hard-reset the entire database; the caches are derived so a wipe is safer than
    // shipping bespoke migrations.
    await database.remove();
    const freshDatabase = await createRxDatabase({
      name: DB_NAME,
      storage,
      multiInstance,
      eventReduce: true,
      ignoreDuplicate: false,
    });
    const freshCollections = await freshDatabase.addCollections({
      [METADATA_COLLECTION_ID]: { schema: metadataSchema },
    });
    const freshMetadataCandidate: unknown = freshCollections[METADATA_COLLECTION_ID];
    assertRxCollection<MetadataDocument>(freshMetadataCandidate, METADATA_COLLECTION_ID);
    const freshMetadata: RxCollection<MetadataDocument> = freshMetadataCandidate;
    const freshCollectionMap = new Map<string, AnyRxCollection>([
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      [METADATA_COLLECTION_ID, freshMetadata as unknown as AnyRxCollection],
    ]);
    await freshMetadata.upsert({ key: SCHEMA_VERSION_KEY, valueNumber: SCHEMA_VERSION });
    await freshMetadata.upsert({ key: ADAPTER_KEY, valueString: adapter });
    return {
      adapter,
      database: freshDatabase,
      collections: freshCollectionMap,
      tanstackCollections: new Map<string, AnyTanStackCollection>(),
    } satisfies LocalCacheState;
  }

  await metadataCollection.upsert({ key: SCHEMA_VERSION_KEY, valueNumber: SCHEMA_VERSION });
  await metadataCollection.upsert({ key: ADAPTER_KEY, valueString: adapter });

  const collectionMap = new Map<string, AnyRxCollection>([
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    [METADATA_COLLECTION_ID, metadataCollection as unknown as AnyRxCollection],
  ]);

  return {
    adapter,
    database,
    collections: collectionMap,
    tanstackCollections: new Map<string, AnyTanStackCollection>(),
  } satisfies LocalCacheState;
}

async function ensureDatabase(): Promise<LocalCacheState> {
  if (!globalState.initPromise) {
    // ast-grep prefer-web-api: this memoizes the IndexedDB bootstrap so multiple callers reuse the same promise.
    // We intentionally guard it here; there is no native replacement that retains the shared state contract.
    globalState.initPromise = (async () => {
      const state = await createDatabase();
      globalState.resolved = state;
      await registerRegisteredCollections(state);
      startPruneLoop();
      return state;
    })();
  }

  const state = await globalState.initPromise;
  return state;
}

async function registerRegisteredCollections(state: LocalCacheState): Promise<void> {
  const pending: Promise<unknown>[] = [];
  for (const [collectionId, storedEntry] of globalState.registry.entries()) {
    if (collectionId === METADATA_COLLECTION_ID) {
      continue;
    }
    const typedEntry = getRegisteredCollectionEntry<GenericDocument>(storedEntry, collectionId);
    pending.push(ensureCollection<GenericDocument>(state, collectionId, typedEntry).then(() => null));
  }
  await Promise.all(pending);
}

async function ensureCollection<T extends Record<string, unknown>>(
  state: LocalCacheState,
  collectionId: string,
  entry: RegisteredCollectionEntry<T>
): Promise<{ adapter: LocalCacheAdapter; rxCollection: RxCollection<T>; collection: Collection<T, string> }> {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const existingCollection = state.collections.get(collectionId) as RxCollection<T> | undefined;
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const existingTanStack = state.tanstackCollections.get(collectionId) as Collection<T, string> | undefined;
  if (existingCollection && existingTanStack) {
    try {
      assertRxCollection<T>(existingCollection, collectionId);
      assertTanStackCollection<T>(existingTanStack, collectionId);
      return {
        adapter: state.adapter,
        rxCollection: existingCollection,
        collection: existingTanStack,
      };
    } catch (error) {
      debug?.warn?.('Invalid TanStack cache entry detected for %s; recreating.', collectionId, error);
      state.collections.delete(collectionId);
      state.tanstackCollections.delete(collectionId);
    }
  }

  // Collections are registered lazily so background caches don't inflate bundle or IndexedDB size until used.
  const added = (await state.database.addCollections({
    [collectionId]: { schema: entry.schema },
  })) as Record<string, RxCollection<T>>;
  const rxCollectionCandidate = added[collectionId];
  assertRxCollection<T>(rxCollectionCandidate, collectionId);
  const rxCollection = rxCollectionCandidate;
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  state.collections.set(collectionId, rxCollection as AnyRxCollection);

  const tanstackCollection = entry.buildTanStackCollection(rxCollection);
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  state.tanstackCollections.set(collectionId, tanstackCollection as AnyTanStackCollection);

  entry.onReady?.({ adapter: state.adapter, rxCollection, collection: tanstackCollection });

  return { adapter: state.adapter, rxCollection, collection: tanstackCollection };
}

export async function registerLocalCacheCollection<T extends Record<string, unknown>>(
  collectionId: string,
  options: {
    schema: RxJsonSchema<T>;
    buildTanStackCollection?: (rxCollection: RxCollection<T>) => Collection<T, string>;
    onReady?: (context: {
      adapter: LocalCacheAdapter;
      rxCollection: RxCollection<T>;
      collection: Collection<T, string>;
    }) => void;
    prune?: (context: { adapter: LocalCacheAdapter; rxCollection: RxCollection<T> }) => Promise<void> | void;
    startSync?: boolean;
  }
): Promise<{ adapter: LocalCacheAdapter; rxCollection: RxCollection<T>; collection: Collection<T, string> }> {
  if (collectionId === METADATA_COLLECTION_ID) {
    throw new Error(`Collection id "${METADATA_COLLECTION_ID}" is reserved.`);
  }

  if (!globalState.registry.has(collectionId)) {
    const startSync = options.startSync ?? !cacheGlobal.__SWEETISTICS_FORCE_MEMORY_RXDB__;
    const entryBase: RegisteredCollectionEntry<T> = {
      schema: options.schema,
      buildTanStackCollection:
        options.buildTanStackCollection ??
        ((rxCollection) =>
          // TanStack DB stays our public API; RxDB only drives persistence + replication events.
          createCollection(
            rxdbCollectionOptions({
              id: collectionId,
              rxCollection,
              startSync,
              utils: {},
            })
          )),
      startSync,
    };
    if (options.onReady) {
      entryBase.onReady = options.onReady;
    }
    if (options.prune) {
      entryBase.prune = options.prune;
    }
    const entry = entryBase;
    globalState.registry.set(collectionId, entry);
  }

  const state = await ensureDatabase();
  const entry = getRegisteredCollectionEntry<T>(globalState.registry.get(collectionId), collectionId);
  return ensureCollection(state, collectionId, entry);
}

export function getTanStackCollection<T extends Record<string, unknown>>(
  collectionId: string
): Collection<T, string> | null {
  const resolved = globalState.resolved;
  if (!resolved) {
    return null;
  }
  const collection = resolved.tanstackCollections.get(collectionId);
  if (!collection) {
    return null;
  }
  assertTanStackCollection<T>(collection, collectionId);
  return collection;
}

export async function getRxdbCollection<T extends Record<string, unknown>>(
  collectionId: string
): Promise<RxCollection<T>> {
  const state = await ensureDatabase();
  const existing = state.collections.get(collectionId);
  if (!existing) {
    throw new Error(`Collection "${collectionId}" has not been registered.`);
  }
  assertRxCollection<T>(existing, collectionId);
  return existing;
}

export async function getLocalCacheAdapter(): Promise<LocalCacheAdapter> {
  const state = await ensureDatabase();
  return state.adapter;
}

export function registerRxdbSQLiteStorage(
  factory: () => Promise<{ storage: RxStorage<unknown, unknown>; multiInstance?: boolean }>
): void {
  globalState.sqliteStorageFactory = factory;
  if (globalState.initPromise !== null) {
    // Recreate database on next init to pick up the new storage.
    globalState.initPromise = null;
    globalState.resolved = null;
  }
}

function startPruneLoop(): void {
  if (globalThis.window === undefined) {
    return;
  }
  if (globalState.pruneTimer !== null) {
    return;
  }
  globalState.pruneTimer = globalThis.setInterval(() => {
    if (!globalState.resolved) {
      return;
    }
    for (const [collectionId, storedEntry] of globalState.registry.entries()) {
      const typedEntry = getRegisteredCollectionEntry<GenericDocument>(storedEntry, collectionId);
      if (!typedEntry.prune) continue;
      const rxCollection = globalState.resolved.collections.get(collectionId);
      if (!rxCollection) continue;
      assertRxCollection<GenericDocument>(rxCollection, collectionId);
      // Let pruning run fire-and-forget so cache eviction never blocks UI interactions.
      Promise.resolve(typedEntry.prune({ adapter: globalState.resolved.adapter, rxCollection })).catch(
        (error: unknown) => {
          debug?.warn?.('Failed to prune %s', collectionId, error);
        }
      );
    }
  }, CLEANUP_INTERVAL_MS);
}

export const METADATA_KEY_SCHEMA_VERSION = SCHEMA_VERSION_KEY;
function assertRxCollection<T extends Record<string, unknown>>(
  value: unknown,
  collectionId: string
): asserts value is RxCollection<T> {
  if (!value || typeof value !== 'object') {
    throw new TypeError(`Collection "${collectionId}" is not initialized.`);
  }
  const candidate = value as { findOne?: unknown };
  if (typeof candidate.findOne !== 'function') {
    throw new TypeError(`Collection "${collectionId}" is not initialized.`);
  }
}

function assertTanStackCollection<T extends Record<string, unknown>>(
  value: unknown,
  collectionId: string
): asserts value is Collection<T, string> {
  if (!value || typeof value !== 'object') {
    throw new TypeError(`TanStack collection "${collectionId}" is not initialized.`);
  }
  const candidate = value as {
    get?: unknown;
    has?: unknown;
    insert?: unknown;
    update?: unknown;
    delete?: unknown;
    entries?: unknown;
  };
  const hasCoreCrudMethods =
    typeof candidate.get === 'function' &&
    typeof candidate.has === 'function' &&
    typeof candidate.insert === 'function' &&
    typeof candidate.update === 'function' &&
    typeof candidate.delete === 'function';
  const hasIterationHelpers = typeof candidate.entries === 'function';
  if (!(hasCoreCrudMethods && hasIterationHelpers)) {
    if (cacheGlobal.__SWEETISTICS_FORCE_MEMORY_RXDB__) {
      return;
    }
    throw new TypeError(`TanStack collection "${collectionId}" is not initialized.`);
  }
}
